import { insertCSS } from "utils";
import { queryToObject, updateQuery } from "utils";

// Switches tabs containers and applies active class.
// USAGE:
//
//  (JS)
//
//  import TabsManager from "tabs-manager";
//  ...
//
//  const tabs = TabsManager({
//    mobileBreakpoint: 760,
//    useUrlState: true,
//  });
//  ...
//
//  tabs.initialize();
//
//  (HTML)
//
//  <button data-tab-trigger="tab-1">Show Tab 1</button>
//  <button data-tab-trigger="tab-2">Show Tab 2</button>
//
//  <div data-tab="tab-1">...</div>
//  <div data-tab="tab-2" data-tab-init="true">...</div>
//

const css = (mobileBreakpoint) => `

  *[data-tab] {
    display: none;
  }

  *[data-tab].active {
    display: flex;
    flex-direction: column;
  }

  @media (max-width: ${mobileBreakpoint}px) {
    *[data-tab] {
      display: flex;
      flex-direction: column;

      top: 100%;
      left: 0;
      right: 0;
      bottom: 0;

      z-index: 100;
      position: fixed;

      margin: 0;
      overflow-y: auto;
      overflow-x: hidden;
      background-color: white;
      transition: top .5s ease;
    }

    *[data-tab].static {
      transition: unset !important;
    }

    *[data-tab].active {
      top:0;
    }
  }
`;

const PIE_MOBILE_BREAKPOINT = 1070;

export default function TabsManager(config = {}) {
  const { mobileBreakpoint = PIE_MOBILE_BREAKPOINT, useUrlState = false } =
    config;

  insertCSS(css(mobileBreakpoint), "tabs-styles");

  const tab = useUrlState ? queryToObject(window.location.search) : undefined;
  const tabs = elementsTolist("[data-tab]", getTab);
  const triggers = elementsTolist("[data-tab-trigger]", getTrigger);

  let [currentTab, currentTrigger] = getCurrentTabAndTrigger(
    tab,
    tabs,
    triggers
  );

  function initialize() {
    // 📒 NOTE:
    // Disable automatic tabs selection on mobile to prevent tabs from covering main page content on load.
    const IS_DESKTOP = window.innerWidth > mobileBreakpoint;
    IS_DESKTOP && selectTab(currentTab, currentTrigger);
  }

  document.addEventListener("click", (event) => {
    if (event.target.matches("button[data-tab-trigger]")) {
      const tab = event.target.dataset.tabTrigger;
      selectTab(tabs[tab], event.target);
      useUrlState && pushTabParam(tab);
    } else if (event.target.matches("button[data-tab-close]")) {
      deselectTabs();
    }
  });

  useUrlState &&
    window.addEventListener("popstate", () => {
      const { tab } = queryToObject(window.location.search);

      if (tab) {
        selectTab(tabs[tab], triggers[tab]);
      } else {
        const [currentTab, currentTrigger] = getCurrentTabAndTrigger(
          tab,
          tabs,
          triggers
        );
        selectTab(currentTab, currentTrigger);
      }
    });

  function selectTab(tab, button) {
    currentTrigger?.classList.remove("active");
    currentTab?.classList.remove("active");

    currentTrigger = button;
    currentTab = tab;

    currentTab?.classList.add("active");
    currentTrigger?.classList.add("active");
  }

  function deselectTabs() {
    currentTrigger?.classList.remove("active");
    currentTab?.classList.remove("active");
  }

  initialize();

  return {
    initialize,
  };
}

// ---- Helpers ----------------

function elementsTolist(selector, processor) {
  return Array.from(document.querySelectorAll(selector)).reduce(processor, {});
}

function getTab(acc, el) {
  acc[el.dataset.tab] = el;
  return acc;
}

function getTrigger(acc, el) {
  acc[el.dataset.tabTrigger] = el;
  return acc;
}

function pushTabParam(name) {
  const query = updateQuery(window.location.search, "tab", name);
  window.history.pushState({}, "", `${window.location.pathname}?${query}`);
}

function getCurrentTabAndTrigger(tab, tabs, triggers) {
  const tabName =
    tab ||
    Object.values(tabs).find((t) => t.dataset.tabInit)?.dataset.tab ||
    Object.keys(tabs)[0];

  const currentTab = tabs[tabName];
  const currentTrigger = triggers[tabName];
  return [currentTab, currentTrigger];
}
